<template>
    <div class="account">
        <Portal v-if="showDeleteMobileErrorModal" to="modal">
            <ModalContainer
                key="deleteMobileErrorModal"
                :is-dark-mode="isDarkMode"
                @close="showDeleteMobileErrorModal = false"
            >
                <template #modal>
                    <Modal
                        class="account__modal"
                        :show-close-button="true"
                        :is-dark-mode="isDarkMode"
                        @close="showDeleteMobileErrorModal = false"
                    >
                        <div class="account__modal-title">
                            Unable to Delete Account
                        </div>
                        <div v-dark class="account__modal-message">
                            You must cancel all subscriptions before deleting your account. Please visit the Settings
                            tab on your mobile app.
                        </div>
                        <div class="account__modal-buttons">
                            <PocketButton
                                class="account__modal-button"
                                type="secondary"
                                :is-dark-mode="isDarkMode"
                                @click="openHelpCenter"
                            >
                                Help Center
                            </PocketButton>
                            <PocketButton
                                class="account__modal-button"
                                :is-dark-mode="isDarkMode"
                                @click="showDeleteMobileErrorModal = false"
                            >
                                Close
                            </PocketButton>
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
        <Portal v-else-if="showDeleteStripeErrorModal" to="modal">
            <ModalContainer
                key="deleteStripeErrorModal"
                :is-dark-mode="isDarkMode"
                @close="showDeleteStripeErrorModal = false"
            >
                <template #modal>
                    <Modal
                        class="account__modal"
                        :show-close-button="true"
                        :is-dark-mode="isDarkMode"
                        @close="showDeleteStripeErrorModal = false"
                    >
                        <div class="account__modal-title">
                            Unable to Delete Account
                        </div>
                        <div v-dark class="account__modal-message">
                            You must cancel all subscriptions before deleting your account. Please visit,
                            <PocketLink
                                @click="$router.push({ name: 'purchases' })"
                                @keydown.enter="$router.push({ name: 'purchases' })"
                            >
                                <!-- eslint-disable -->Purchases</PocketLink><!-- eslint-enable -->
                            and click "edit" to cancel any active subscriptions.
                        </div>
                        <div class="account__modal-buttons">
                            <PocketButton
                                class="account__modal-button"
                                type="secondary"
                                :is-dark-mode="isDarkMode"
                                @click="openHelpCenter"
                            >
                                Help Center
                            </PocketButton>
                            <PocketButton
                                class="account__modal-button"
                                :is-dark-mode="isDarkMode"
                                @click="showDeleteStripeErrorModal = false"
                            >
                                Close
                            </PocketButton>
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
        <Portal v-if="showDeleteConfirmModal" to="modal">
            <ModalContainer
                key="confirmDeleteAccountModal"
                :is-dark-mode="isDarkMode"
                @close="showDeleteConfirmModal = false"
            >
                <template #modal>
                    <Modal
                        class="account__modal"
                        :show-close-button="true"
                        :is-dark-mode="isDarkMode"
                        @close="showDeleteConfirmModal = false"
                    >
                        <div class="account__modal-title">
                            Delete Your Pocket Prep Account?
                        </div>
                        <div v-dark class="account__modal-message">
                            You will lose access to all of your data. This is irreversible.
                        </div>
                        <div class="account__modal-buttons">
                            <PocketButton
                                class="account__modal-button"
                                type="secondary"
                                :is-dark-mode="isDarkMode"
                                @click="showDeleteConfirmModal = false"
                            >
                                Never mind
                            </PocketButton>
                            <PocketButton
                                type="primary-red"
                                class="account__modal-button"
                                :is-loading="isLoading"
                                :is-dark-mode="isDarkMode"
                                @click="deleteAccount"
                            >
                                Yes, Delete
                            </PocketButton>
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
        <Portal v-if="showForgotPasswordModal" to="modal">
            <ModalContainer
                key="forgotPasswordModal"
                :is-dark-mode="isDarkMode"
                @close="showForgotPasswordModal = false"
            >
                <template #modal>
                    <Modal
                        class="account__modal"
                        :show-close-button="true"
                        :is-dark-mode="isDarkMode"
                        @close="showForgotPasswordModal = false"
                    >
                        <div class="account__modal-title">
                            Forgot Your Password?
                        </div>
                        <div v-dark class="account__modal-message">
                            We’ll send a reset password link to {{ originalEmail }}.
                        </div>
                        <div class="account__modal-buttons">
                            <PocketButton
                                class="account__modal-button"
                                :is-dark-mode="isDarkMode"
                                type="secondary"
                                @click="showForgotPasswordModal = false"
                            >
                                Cancel
                            </PocketButton>
                            <PocketButton
                                class="account__modal-button"
                                :is-loading="isSendingPasswordLink"
                                :is-dark-mode="isDarkMode"
                                @click="sendResetPasswordLink"
                            >
                                Send Reset Password Link
                            </PocketButton>
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
        <Portal v-if="showEmailSentModal" to="modal">
            <ModalContainer
                key="emailSentModal"
                :is-dark-mode="isDarkMode"
                @close="showEmailSentModal = false"
            >
                <template #modal>
                    <Modal
                        class="account__modal"
                        :show-close-button="true"
                        :is-dark-mode="isDarkMode"
                        @close="showEmailSentModal = false"
                    >
                        <div class="account__modal-title">
                            Email Sent
                        </div>
                        <div v-dark class="account__modal-message">
                            <Icon type="check" />
                        </div>
                        <div class="account__modal-buttons">
                            <PocketButton
                                class="account__modal-button"
                                type="secondary"
                                :is-dark-mode="isDarkMode"
                                @click="showIntercom"
                            >
                                Contact Support
                            </PocketButton>
                            <PocketButton
                                class="account__modal-button"
                                :is-loading="isSendingPasswordLink"
                                :is-dark-mode="isDarkMode"
                                @click="sendResetPasswordLink"
                            >
                                Resend Link
                            </PocketButton>
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
        <SettingsHead />
        <h2 v-dark class="account__label">
            Profile
        </h2>
        <PocketInput
            v-model="firstName"
            label="First Name"
            class="account__field"
            :error="errorFields.includes('firstName')"
            :is-dark-mode="isDarkMode"
            @keydown.enter="saveChanges"
        />
        <PocketInput
            v-model="lastName"
            label="Last Name"
            class="account__field"
            :error="errorFields.includes('lastName')"
            :is-dark-mode="isDarkMode"
            @keydown.enter="saveChanges"
        />
        <PocketInput
            v-model="email"
            field-type="email"
            label="Email"
            class="account__field"
            :error="errorFields.includes('email')"
            :is-dark-mode="isDarkMode"
            @keydown.enter="saveChanges"
        />
        <template v-if="showPasswordFields">
            <div class="account__password-label">
                Update Password
            </div>
            <PocketInput
                v-model="currentPassword"
                field-type="password"
                label="Current Password"
                class="account__field"
                :error="errorFields.includes('currentPassword')"
                :is-dark-mode="isDarkMode"
                @keydown.enter="saveChanges"
            />
            <PocketButton
                v-if="showPasswordFields && breakpoint === 'black-bear'"
                type="tertiary-small"
                class="account__small-breakpoint-forgot"
                :is-dark-mode="isDarkMode"
                @click="showForgotPasswordModal = true"
            >
                Forgot Password?
            </PocketButton>
            <PocketInput
                v-model="newPassword"
                field-type="password"
                label="New Password"
                labelSubtext="10+ characters, including uppercase and a number"
                class="account__field"
                :error="errorFields.includes('newPassword')"
                :is-dark-mode="isDarkMode"
                @keydown.enter="saveChanges"
            />
        </template>
        <Errors
            v-if="errors.length"
            class="account__errors"
            :class="{ 'account__errors--password-errors': showPasswordErrorHeader }"
            :errors="errors"
            :error-header="errorsHeader"
            :is-dark-mode="isDarkMode"
        />
        <div class="account__buttons" :class="{ 'account__buttons--expanded': showPasswordFields }">
            <PocketButton
                v-if="!showPasswordFields"
                type="tertiary"
                :is-dark-mode="isDarkMode"
                @click="showPasswordFields = true"
            >
                Update Password
            </PocketButton>
            <PocketButton
                v-else-if="showPasswordFields && breakpoint !== 'black-bear'"
                type="tertiary-small"
                :is-dark-mode="isDarkMode"
                @click="showForgotPasswordModal = true"
            >
                Forgot Password?
            </PocketButton>
            <div
                class="account__buttons-save"
                :class="{
                    'account__buttons-save--expanded': showPasswordFields,
                    'account__buttons-save--small-breakpoint': showPasswordFields && breakpoint === 'black-bear'
                }"
            >
                <PocketButton
                    v-if="showPasswordFields"
                    :is-dark-mode="isDarkMode"
                    type="tertiary"
                    @click="cancelPasswordChange"
                >
                    Cancel
                </PocketButton>
                <PocketButton
                    :is-dark-mode="isDarkMode"
                    :is-loading="isLoading"
                    :disabled="!hasUnsavedChanges || !firstName.trim() || !lastName.trim()"
                    @click="saveChanges"
                >
                    Save Changes
                </PocketButton>
            </div>
        </div>
        <PocketButton
            type="tertiary-red"
            class="account__delete"
            :class="{ 'account__delete--expanded': showPasswordFields }"
            :is-dark-mode="isDarkMode"
            @click="clickDeleteAccount"
        >
            Delete My Pocket Prep Account
        </PocketButton>
        <h2
            id="preferences"
            v-dark
            class="account__label"
        >
            Preferences
        </h2>
        <div v-dark class="account__preferences-label">
            Notifications
        </div>
        <div class="account__reminders">
            <div class="account__reminders-left">
                <ToggleSwitch
                    v-model="enableReminders"
                    :is-dark-mode="isDarkMode"
                    size="large"
                    aria-label="Email Reminders"
                />
            </div>
            <div class="account__reminders-right">
                <div class="account__reminders-title">
                    Email Reminders
                </div>
                <div v-dark class="account__reminders-description">
                    Nudges to study and encouragement in your inbox
                </div>
                <PocketSelect
                    v-if="enableReminders"
                    v-model="reminderFrequency"
                    :data="reminderOptions"
                    :is-dark-mode="isDarkMode"
                    class="account__field"
                    label="Frequency"
                />
            </div>
        </div>
        <div v-dark class="account__preferences-label account__sort-preferences-label">
            Sort Preferences
        </div>
        <div id="account__sort-label-id" class="account__sort-label">
            Sort <span>Subject Insights</span>
        </div>
        <Radio
            v-model="sortPreference"
            class="account__sort-radio"
            :is-dark-mode="isDarkMode"
            :data="sortOptions"
            labelled-by="account__sort-label-id"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import SettingsHead from '@/components/Settings/SettingsHead.vue'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { isSupportWindow, isValidEmail, isValidPassword, passwordPolicyErrors } from '@/utils'
import type { TUpdateUserPayload } from '@/store/user/actions'
import { toastModule } from '@/store/toast/module'
import { stripeModule } from '@/store/stripe/module'
import { subscriptionModule } from '@/store/subscription/module'
import { screenModule } from '@/store/screen/module'
import type { Study } from '@pocketprep/types'

type TSortOption = { value: 'weakest' | 'strongest' | 'alphabetical'; label: string }
type TReminderOption = { value: 'daily' | 'weekly'; label: 'Daily' | 'Weekly' }

const {
    Input: PocketInput,
    Button: PocketButton,
    Select: PocketSelect,
    ToggleSwitch,
    Errors,
    Icon,
    Radio,
    ModalContainer,
    Modal,
    Link: PocketLink,
} = UIKit

const router = useRouter()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const originalFirstName = ref('')
const originalLastName = ref('')
const originalEmail = ref('')
const currentPassword = ref('')
const newPassword = ref('')
const sortPreference = ref<TSortOption>({ value: 'weakest', label: 'Weakest to Strongest' })
const showPasswordFields = ref(false)
const isLoading = ref(true)
const isSendingPasswordLink = ref(false)
const enableReminders = ref(false)
const showDeleteConfirmModal = ref(false)
const showForgotPasswordModal = ref(false)
const showEmailSentModal = ref(false)
const showDeleteMobileErrorModal = ref(false)
const showDeleteStripeErrorModal = ref(false)
const sortOptions: TSortOption[] = [
    { value: 'weakest', label: 'Weakest to Strongest' },
    { value: 'strongest', label: 'Strongest to Weakest' },
    { value: 'alphabetical', label: 'Alphabetically' },
]
const reminderOptions: TReminderOption[] = [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }]
const reminderFrequency = ref<TReminderOption>({ value: 'daily', label: 'Daily' })
const errorFields = ref<string[]>([])
const errors = ref<string[]>([])
const errorsHeader = ref('')
const showPasswordErrorHeader = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const hasUnsavedChanges = computed(() => {
    return firstName.value.trim() !== originalFirstName.value
        || lastName.value.trim() !== originalLastName.value
        || email.value !== originalEmail.value
        || (showPasswordFields.value && (currentPassword.value || newPassword.value))
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        stripeModule.actions.fetchStripeSubscriptions(),
    ])
    firstName.value = userModule.state.user?.firstName || ''
    lastName.value = userModule.state.user?.lastName || ''
    email.value = userModule.state.user?.email || ''
    originalFirstName.value = firstName.value
    originalLastName.value = lastName.value
    originalEmail.value = email.value
    enableReminders.value = !!userModule.state.user?.studyRemindersFrequency
    reminderFrequency.value = userModule.state.user?.studyRemindersFrequency === 'weekly'
        ? { value: 'weekly', label: 'Weekly' }
        : reminderFrequency.value
    sortPreference.value = sortOptions
        .find(o => o.value === userModule.state.settings.subjectInsightsSort)
        || { value: 'weakest', label: 'Weakest to Strongest' }

    const settingsHeaderEl = document.getElementById('settings')
    // Scroll into view the top of the page on the setting header
    if (settingsHeaderEl && breakpoint.value !== 'black-bear') {
        settingsHeaderEl.scrollIntoView()
    }
    isLoading.value = false
})

const openHelpCenter = () => {
    window.open('https://help.pocketprep.com/en/articles/3697654-how-do-i-cancel-my-subscription', '_blank')
}

const cancelPasswordChange = () => {
    errors.value = []
    errorFields.value = []
    errorsHeader.value = ''
    showPasswordFields.value = false
    showPasswordErrorHeader.value = false
}

const saveChanges = async () => {
    errors.value = []
    errorFields.value = []
    errorsHeader.value = ''
    showPasswordErrorHeader.value = false

    if (!isValidEmail(email.value)) {
        errors.value.push('Invalid email address.')
        errorFields.value.push('email')
    }

    if (!firstName.value || !lastName.value) {
        if (!firstName.value?.trim()) {
            errorFields.value.push('firstName')
        }
        if (!lastName.value?.trim()) {
            errorFields.value.push('lastName')
        }
        errors.value.push('Enter a first and last name.')
    }

    if (showPasswordFields.value && (currentPassword.value || newPassword.value)) {
        if (!currentPassword.value) {
            errors.value.push('Enter your current password.')
            errorFields.value.push('currentPassword')
        }

        if (!isValidPassword(newPassword.value)) {
            errorFields.value.push('newPassword')

            showPasswordErrorHeader.value =
                errorFields.value.length === 1 && errorFields.value.includes('newPassword')
            if (showPasswordErrorHeader.value) {
                errorsHeader.value = 'Password doesn`t meet the following requirements:'
            }
            const passwordErrors = passwordPolicyErrors(showPasswordErrorHeader.value, newPassword.value)

            errors.value = [ ...errors.value, ...passwordErrors ]
        }

        if (!errors.value.length) {
            try {
                await userModule.actions.signIn({ username: originalEmail.value, password: currentPassword.value })
            } catch (e) {
                errors.value.push('Incorrect current password.')
                errorFields.value.push('currentPassword')
            }
        }
    }

    if (!errors.value.length) {
        isLoading.value = true
        const updateUserPayload: TUpdateUserPayload = {
            firstName: firstName.value,
            lastName: lastName.value,
            email: email.value,
        }
        if (currentPassword.value && newPassword.value && showPasswordFields.value) {
            updateUserPayload.password = newPassword.value
        }
        try {
            await userModule.actions.updateUser(updateUserPayload)
            originalFirstName.value = firstName.value
            originalLastName.value = lastName.value
            originalEmail.value = email.value

            if (newPassword.value) {
                newPassword.value = ''
                currentPassword.value = ''
                showPasswordFields.value = false
            }
            toastModule.actions.displayToast({
                title: 'Account information saved successfully',
            })
        } catch (err) {
            const error = (err as { error?: string }).error || (err as { message?: string }).message
            if (!err) {
                /* istanbul ignore next */
                errors.value.push('Unable to update account information.')
            } else if (error) {
                errors.value.push(error)
            } else if (err instanceof Error || typeof err === 'string') {
                errors.value.push(err.toString())
            }
        } finally {
            const newUserData = await userModule.actions.fetchUserData()
            if (newUserData) {
                email.value = newUserData?.user.email
            }
            isLoading.value = false
        }
    }
}

const sendResetPasswordLink = async () => {
    isSendingPasswordLink.value = true
    await userModule.actions.resetPassword()
    showForgotPasswordModal.value = false
    showEmailSentModal.value = true
    isSendingPasswordLink.value = false
}

const clickDeleteAccount = () => {
    // if any non-Stripe active subscriptions, show error modal
    const renewingSub = subscriptionModule.state.subscriptions.find(s => s.willAutoRenew)
    if (renewingSub) {
        if ((renewingSub.receipt as Study.Class.ReceiptJSON).source === 'Stripe') {
            showDeleteStripeErrorModal.value = true
        } else {
            showDeleteMobileErrorModal.value = true
        }
    } else {
        showDeleteConfirmModal.value = true
    }
}

const deleteAccount = async () => {
    isLoading.value = true

    await userModule.actions.deleteUser()

    router.push({ name: 'sign-in', query: { delete: 'success' } })
}

const showIntercom = () => {
    /* istanbul ignore next */
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }

    showEmailSentModal.value = false
}

const updateSortPreference = () => {
    userModule.actions.updateLocalSettings({
        subjectInsightsSort: sortPreference.value.value,
    })
}

const updateNotificationPreference = async () => {
    const reminderFrequencyValue = enableReminders.value ? reminderFrequency.value.value : 'never'
    await userModule.actions.updateReminderPreferences(reminderFrequencyValue)
}

watch(sortPreference, updateSortPreference)

watch([ enableReminders, reminderFrequency ], updateNotificationPreference)
</script>
<style lang="scss" scoped>
.account {
    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
        width: 392px;
        padding: 35px 0 26px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            max-height: 638px;
            top: 10px;
        }
    }

    &__modal-title {
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        width: 309px;
        margin: 0 auto 12px;
    }

    &__modal-message {
        font-size: 15px;
        line-height: 22px;
        color: $slate-03;
        margin-bottom: 29px;
        width: 309px;

        &--dark {
            color: $white;
        }

        a {
            color: $brand-blue;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }

        svg {
            width: 31px;
            height: 28px;
            color: $green;
            margin-top: 13px;
        }
    }

    &__modal-buttons {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__modal-button {
        margin: 0 6px;
    }

    &__label {
        font-size: 16px;
        line-height: 19px;
        font-weight: 600;
        margin: 0 0 19px;

        &--dark {
            color: rgba($white, 0.86);
        }
    }

    &__field {
        width: 381px;
        margin: 0 -12px 12px;

        @include breakpoint(black-bear) {
            width: calc(100% + 24px);
        }
    }

    &__small-breakpoint-forgot {
        margin-bottom: 24px;
    }

    &__buttons {
        display: flex;
        max-width: 381px;
        width: 100%;
        justify-content: space-between;
        margin-top: 20px;
        margin-bottom: 24px;

        &--expanded {
            margin-left: -2px;
        }

        @include breakpoint(black-bear) {
            max-width: none;
        }
    }

    &__buttons-save {
        width: 140px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 9px;

        @include breakpoint(black-bear) {
            margin-right: -15px;
        }

        &--expanded {
            width: 216px;
            justify-content: space-between;
        }

        &--small-breakpoint {
            margin-left: auto;
        }

        svg {
            width: 23px;
            height: 23px;
        }
    }

    &__errors {
        width: 381px;
        margin: 0 -12px;
        box-sizing: border-box;

        @include breakpoint(brown-bear) {
            width: calc(100% + 24px);
        }

        &--password-errors {
            :deep(.uikit-errors__error) {
                &::before {
                    content: '•';
                    display: inline-block;
                    margin-right: 5px;
                }
            }
        }
    }

    &__delete {
        margin-bottom: 45px;

        &--expanded {
            margin-left: -3px;
        }
    }

    &__preferences-label {
        font-size: 14px;
        line-height: 17px;
        color: $slate-01;
        padding: 7px 11px;
        margin-left: -12px;
        margin-right: -12px;
        border-bottom: 1px solid $gray-divider;
        margin-bottom: 28px;

        &--dark {
            color: $pewter;
            border-bottom-color: rgba($pewter, 0.6);
        }
    }

    &__reminders {
        display: flex;

        @include breakpoint(black-bear) {
            display: block;
            position: relative;
        }
    }

    &__reminders-left {
        margin-right: 15px;

        @include breakpoint(black-bear) {
            position: absolute;
            right: -12px;
            top: 2px;
            margin-right: 0;
        }
    }

    &__reminders-title {
        font-size: 15px;
        line-height: 18px;
        margin-bottom: 4px;
    }

    &__reminders-description {
        font-size: 14px;
        line-height: 18px;
        color: $slate-03;
        margin-bottom: 23px;

        &--dark {
            color: $pewter;
        }

        @include breakpoint(black-bear) {
            max-width: 263px;
        }
    }

    &__sort-preferences-label {
        margin-top: 34px;
    }

    &__color-preferences-label {
        margin-top: 42px;
    }

    &__sort-label {
        font-size: 15px;
        margin-bottom: 10px;

        span {
            font-weight: 600;
        }
    }

    &__color-radio {
        :deep(ul) {
            display: flex;

            li:first-child {
                margin-right: 52px;
            }
        }
    }

    &__color-radio-label {
        font-size: 15px;
        line-height: 18px;
        margin-bottom: 3px;
    }

    &__color-radio-subtext {
        font-size: 14px;
        line-height: 22px;
        color: $slate-03;
        margin-bottom: 10px;
    }

    &__color-radio-image {
        width: 232px;
        height: 94px;
    }

    &__password-label {
        font-size: 14px;
        line-height: 17px;
        font-weight: 600;
        margin-top: 29px;
        margin-bottom: 19px;
    }
}
</style>
